import React from 'react';
import I18n from 'i18n-js';
import {
  core_apimessages_Channel,
  CommonsOffersCreateOfferForm,
  core_apimessages_NegotiationParty,
  Listing,
  core_apimessages_BumpKey,
} from '../rql';
import bind from '../bind';
import SignupSigninModal from '../components/signup_signin_modal';
import { IUserContext, withUserContext } from '../components/user_context_provider';
import Modal from '../components/modal';
import OfferCreateForm from './offer_create_form';
import { MParticleEventName, trackEvent } from '../elog/mparticle_tracker';
import { getOfferNudgeText } from '../nudge_manager';
import { RCAlertBox } from '../rc/RCAlertBox';
import gql from 'graphql-tag';
import experiments from '../experiments';
import { formatExperimentsForEventAttributes } from '../elog/mparticle';

interface IExternalProps {
  listingId: string;
  isSeller?: boolean;
  onOfferSuccess?: Function;
  listingThumbnail?: string;
  enableNudge?: boolean;
  disabled?: boolean;
  listing?: Listing;
  offerCount?: number;
  position?: number;
  parentComponentName?: string;
  bumpKey?: core_apimessages_BumpKey;
  userTotalOffersCount?: number;
}

type IProps = IExternalProps & IUserContext;

interface IState {
  openDialog: boolean;
}

export const Item2LoggedInOffersCountFragment = gql`
  fragment Item2LoggedInOffersCountData on rql_Me {
    id
    profile {
      totalBuyerOffersCreated
    }
  }
`;

const trackedExperiments = [];

function setTrackedExperiments(props: IProps) {
  if (!props.user.loggedOut && props.userTotalOffersCount === 0) {
    trackedExperiments.push(experiments.OPEN_TO_OFFERS_LOGGED_IN);
  }
}

export class CreateOfferButton extends React.Component<IProps, IState> {
  state: IState = {
    openDialog: false,
  };

  @bind
  toggleModal(e) {
    // When handle closing the modal, it will blow up because there is no event being passed in
    // so we have to guard
    e.preventDefault();
    e.stopPropagation();

    this.setState(
      state => ({ openDialog: !state.openDialog }),
      () => {
        if (this.state.openDialog) {
          setTrackedExperiments(this.props);

          trackEvent({
            eventName: MParticleEventName.ClickedOfferButton,
            listingId: this.props.listingId,
            listing: this.props.listing,
            position: this.props.position,
            componentName: this.props.parentComponentName,
            experiments: formatExperimentsForEventAttributes(this.props.user, trackedExperiments),
          });
        }
      },
    );
  }

  @bind
  handleClose() {
    this.setState({ openDialog: false });
  }

  @bind
  handleSuccess(negotiation: CommonsOffersCreateOfferForm.Negotiations) {
    this.handleClose();

    const otherParty =
      negotiation.lastOffer.initiatingParty === core_apimessages_NegotiationParty.BUYER ?
        I18n.t('commons.offers.seller') :
        I18n.t('commons.offers.buyer');

    const initialOfferMessage = I18n.t('commons.offers.initialSubmissionSuccess', {
      otherParty,
      amount: negotiation.lastOffer.prices.totalPrice.display.display,
    });

    if (this.props.onOfferSuccess) {
      this.props.onOfferSuccess(initialOfferMessage);
    }
  }

  renderOfferSignupCallout() {
    return (
      <div className="mb-2">
        <RCAlertBox type="success">
          <h4 className="size-110 weight-bold mb-1">
            {I18n.t('commons.offers.benefits.title')}
          </h4>
          <ul className="fa-ul">
            <li>
              <span className="fa fa-check fa-li color-green" />
              {I18n.t('commons.offers.benefits.saveMoney')}
            </li>
            <li>
              <span className="fa fa-check fa-li color-green" />
              {I18n.t('commons.offers.benefits.quickResponse')}
            </li>
          </ul>
        </RCAlertBox>
      </div>
    );
  }

  renderNudge() {
    if (!this.props.enableNudge) return null;
    const nudgeText = getOfferNudgeText(this.props.user, this.props.userTotalOffersCount);
    if (!nudgeText) return null;

    return (
      <div className="offer-action__nudge">
        {nudgeText}
      </div>
    );
  }

  renderSignupSigninModalOrOfferModal() {
    if (this.props.user.loggedOut) {
      return (
        <SignupSigninModal
          active={this.state.openDialog}
          onRequestClose={this.handleClose}
          trackSource="Offer"
          messageComponent={this.renderOfferSignupCallout()}
        />
      );
    }
    return (
      <Modal
        onRequestClose={this.handleClose}
        isOpen={this.state.openDialog}
        headerTitle={I18n.t('commons.offers.action.create')}
        paddedBody
        newClose
      >
        <OfferCreateForm
          listingId={this.props.listingId}
          isSeller={this.props.isSeller}
          shopChannel={core_apimessages_Channel.REVERB}
          onSuccess={this.handleSuccess}
          onCancel={this.handleClose}
          isDisplayed={this.state.openDialog}
          bumpKey={this.props.bumpKey}
        />
      </Modal>
    );
  }

  render() {
    return (
      <div>
        <button
          className="offer-action"
          data-disabled={this.props.disabled || null}
          onClick={this.toggleModal} type="button">
          {I18n.t('commons.offers.action.create')}
        </button>
        {!!this.props.offerCount &&
          <div
            data-offer-count
            className="size-80 opacity-70 mt-half align-center"
          >
            {I18n.t('commons.offers.offerCount', { count: this.props.offerCount })}
          </div>
        }
        {this.renderSignupSigninModalOrOfferModal()}
        {this.renderNudge()}
      </div>
    );
  }
}

export default withUserContext<IExternalProps>(CreateOfferButton);
